/**
 * Frontend mirror of the backend transition matrix in
 * ``backend/api/routes/admin/exceptions_transitions.py``.
 *
 * The drawer's status menu (ExceptionDrawer.tsx) reads from this
 * spec to decide which menu items to enable and what label to show.
 * Bulk callers (future) will read the same spec. The backend
 * re-validates against its own matrix so the FE can't smuggle an
 * invalid transition; this spec exists purely so the UI offers the
 * right affordances without re-implementing the rules.
 *
 * **Stay in sync.** Any new transition added on the backend must
 * mirror here (and vice versa). The pair is small enough that drift
 * is obvious in code review.
 */
import type { LifecycleStatus } from './issuesState'

export type TransitionAction = 'block' | 'unblock' | 'ignore' | 'reopen'

/**
 * Per-row kebab action: any lifecycle transition plus the orthogonal
 * ``mark-fixed`` / ``unmark-fixed`` annotations (open rows only). Single
 * source so the drawer's kebab and ``useDrawerObjectActions`` agree on one
 * set.
 */
export type RowAction = TransitionAction | 'mark-fixed' | 'unmark-fixed'

export interface TransitionMeta {
  /** Action name sent to POST /admin/exceptions/{id}/transition. */
  action: TransitionAction
  /** Lifecycle status the transition aims at. Used for menu chip
   * enabled state - the chip for ``targetStatus`` lights up when
   * ``available.some(t => t.targetStatus === chipStatus)``. */
  targetStatus: LifecycleStatus
  /** Wording shown when the menu item is hoverable (transition
   * available). Action-oriented; the current state's own chip shows
   * the plain status name from STATUS_LABEL. */
  label: string
  /** Whether the FE must prompt for a free-text reason before
   * submitting. Mirrors the backend's spec.requires_reason. */
  requiresReason: boolean
}

/**
 * From-status -> available transitions. Resolved rows have no
 * user-initiated transitions (system-managed); their entry is the
 * empty array so callers don't have to special-case ``undefined``.
 *
 * Order within each array determines menu order.
 */
export const TRANSITIONS: Record<LifecycleStatus, TransitionMeta[]> = {
  needs_review: [
    {
      action: 'block',
      targetStatus: 'blocked',
      label: "Can't fix yet",
      requiresReason: true,
    },
    {
      action: 'ignore',
      targetStatus: 'ignored',
      label: 'Ignore',
      requiresReason: true,
    },
  ],
  blocked: [
    {
      action: 'unblock',
      targetStatus: 'needs_review',
      label: 'Remove block',
      requiresReason: false,
    },
    {
      action: 'ignore',
      targetStatus: 'ignored',
      label: 'Ignore',
      requiresReason: true,
    },
  ],
  ignored: [
    {
      action: 'reopen',
      targetStatus: 'needs_review',
      label: 'Reopen for review',
      requiresReason: false,
    },
    {
      action: 'block',
      targetStatus: 'blocked',
      label: "Can't fix yet",
      requiresReason: true,
    },
  ],
  resolved: [],
}

/**
 * Look up the transition (if any) that moves from ``from`` to
 * ``target``. Returns ``null`` when no user-initiated transition
 * exists - the menu chip for ``target`` should be disabled in that
 * case.
 */
export function findTransition(
  from: LifecycleStatus,
  target: LifecycleStatus,
): TransitionMeta | null {
  if (from === target) return null
  return TRANSITIONS[from]?.find((t) => t.targetStatus === target) ?? null
}
